/**
 * The Composer's Length tools (Dax, 2026-10-04), as arithmetic: every clip lengthened or shortened by the
 * same proportion, so the whole cut moves by it. A clip shortens about its middle and never below a floor;
 * it grows forward, then back, inside its snippet. What growing could not reach is reported, for the
 * person to choose how to find the rest. The server puts every edge on its frames.
 */

/** A small step and a large one: two large steps halve the cut, or double it. */
export const LENGTH_STEP_SMALL = 0.07;
export const LENGTH_STEP_LARGE = 1 - Math.SQRT1_2;
/** No clip is shortened below this. */
export const LENGTH_MIN_CLIP_MS = 300;
/** A shortfall smaller than this is not worth asking about. */
export const LENGTH_SHORTFALL_MIN_MS = 250;

export interface LengthSlot { inMs: number; outMs: number; boundsInMs: number; boundsOutMs: number }

/** Every clip by `factor` (under 1 shortens); returns the new edges and how far the cut fell short of its target. */
export function resizeSlots(slots: readonly LengthSlot[], factor: number): { ranges: { inMs: number; outMs: number }[]; shortMs: number } {
  const total = slots.reduce((acc, s) => acc + (s.outMs - s.inMs), 0);
  const ranges = slots.map((s) => {
    const len = s.outMs - s.inMs;
    const want = Math.max(Math.min(len, LENGTH_MIN_CLIP_MS), Math.round(len * factor));
    if (want <= len) {
      const inMs = s.inMs + Math.floor((len - want) / 2);
      return { inMs, outMs: inMs + want };
    }
    const outMs = Math.min(s.boundsOutMs, s.inMs + want);
    return { inMs: Math.max(s.boundsInMs, outMs - want), outMs };
  });
  const reached = ranges.reduce((acc, r) => acc + (r.outMs - r.inMs), 0);
  return { ranges, shortMs: factor > 1 ? Math.max(0, Math.round(total * factor) - reached) : 0 };
}
